import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { surfaceSx, toneMain } from "./channelTheme";
import { ChannelStatusChip, BackendStateChip } from "./ChannelVisuals";
import ChannelKpiRow from "./ChannelKpiRow";
import ChannelHistoryChart from "./ChannelHistoryChart";

export default function ChannelDetailCard({ id, embedded = false, channel, channels = [], onStep, visual = null }) {
  const [range, setRange] = useState("24h");
  const [compareKeys, setCompareKeys] = useState([]);

  // Never compare a channel with itself; drop channels that disappeared.
  useEffect(() => {
    setCompareKeys((keys) => {
      const next = keys.filter((k) => k !== channel?.key && channels.some((c) => c.key === k));
      return next.length === keys.length ? keys : next;
    });
  }, [channel?.key, channels]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Channel link copied");
    } catch (e) {
      toast.error("Could not copy link");
    }
  };

  const Wrapper = embedded ? MDBox : Paper;
  const wrapperProps = embedded
    ? { id, py: 2 }
    : { id, elevation: 0, sx: (theme) => ({ ...surfaceSx(theme), p: { xs: 2, md: 2.5 }, mb: 3 }) };

  if (!channel) {
    return (
      <Wrapper {...wrapperProps}>
        <MDTypography variant="button" color="text">
          Select a channel to see its details.
        </MDTypography>
      </Wrapper>
    );
  }

  const index = channels.findIndex((c) => c.key === channel.key);

  return (
    <Wrapper {...wrapperProps}>
      <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
        <MDBox
          sx={({ palette, borders, functions: { rgba } }) => ({
            width: 40,
            height: 40,
            display: "grid",
            placeItems: "center",
            borderRadius: borders.borderRadius.md,
            color: toneMain(palette, "info"),
            backgroundColor: rgba(toneMain(palette, "info"), 0.14),
          })}
        >
          <Icon className={channel.icon} sx={{ fontSize: "1.1rem !important", width: "auto" }} />
        </MDBox>
        <MDBox flex="1 1 auto" minWidth={0}>
          <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <MDTypography variant="h5" fontWeight="bold" noWrap>
              {channel.name}
            </MDTypography>
            <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
              #{channel.number}
            </MDTypography>
            <ChannelStatusChip channel={channel} />
            <BackendStateChip channel={channel} />
          </MDBox>
          <MDTypography variant="caption" color="text">
            {channel.kindLabel}
            {channel.role ? ` · ${channel.role === "supply" ? "Supply" : "Holding"} tank` : ""}
            {index >= 0 ? ` · Channel ${index + 1} of ${channels.length} · use ← / → to switch` : ""}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" gap={0.5}>
          <Tooltip title="Previous channel (←)">
            <span>
              <IconButton size="small" onClick={() => onStep?.(-1)} disabled={channels.length < 2} aria-label="Previous channel">
                <Icon>chevron_left</Icon>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next channel (→)">
            <span>
              <IconButton size="small" onClick={() => onStep?.(1)} disabled={channels.length < 2} aria-label="Next channel">
                <Icon>chevron_right</Icon>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Copy link to this channel">
            <IconButton size="small" onClick={copyLink} aria-label="Copy link to this channel">
              <Icon>link</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      </MDBox>

      <ChannelKpiRow channel={channel} />

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} md={visual ? 7 : 12}>
          <ChannelHistoryChart
            channel={channel}
            channels={channels}
            range={range}
            onRangeChange={setRange}
            compareKeys={compareKeys}
            onCompareChange={setCompareKeys}
          />
        </Grid>
        {visual && (
          <Grid item xs={12} md={5}>
            {visual}
          </Grid>
        )}
      </Grid>
    </Wrapper>
  );
}
